import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import 'flag-icons/css/flag-icons.min.css';
import Toast from '../components/Toast';
import { useLanguage } from '../context/LanguageContext';
import logger from '../utils/logger';

function Home() {
  const navigate = useNavigate();
  // const location = useLocation();
  const { t } = useLanguage();
  const [ambassadors, setAmbassadors] = useState([]);
  const [loadingAmbassadors, setLoadingAmbassadors] = useState(true);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [openQuestionIndex, setOpenQuestionIndex] = useState(null);
  
  // State for dynamic first ambassador (same logic as Header)
  // const [firstAmbassador, setFirstAmbassador] = useState(null);
  
  // Modal states
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [streamerName, setStreamerName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Toast states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Default image URL
  const defaultImage = "https://res.cloudinary.com/dez2cotie/image/upload/v1751233643/pngegg_pk3dvl.png";

  // Fetch first ambassador dynamically (same logic as Header)
  /*
  useEffect(() => {
    const fetchFirstAmbassador = async () => {
      try {
        const response = await fetch('/.netlify/functions/get-admin-ambassadors');
        const result = await response.json();
        
        if (result.success && result.data && result.data.length > 0) {
          // Get the first real ambassador (not default data)
          const firstRealAmbassador = result.data.find(ambassador => 
            !ambassador.id.toString().startsWith('default-') && ambassador.owner
          );
          
          if (firstRealAmbassador) {
            setFirstAmbassador(firstRealAmbassador.owner);
          } else {
            // Fallback to first ambassador even if it's default
            setFirstAmbassador(result.data[0]?.owner || 'bob');
          }
        } else {
          // Fallback to 'bob' if no data
          setFirstAmbassador('bob');
        }
      } catch (error) {
        logger.error('Error fetching first ambassador:', error);
        // Fallback to 'bob' on error
        setFirstAmbassador('bob');
      }
    };

    fetchFirstAmbassador();
  }, []);
  */

  // Country mapping to get country code from country name
  const countryMapping = {
    'World': 'W',
    'world': 'W',
    'France': 'FR',
    'france': 'FR',
    'España': 'ES',
    'Spain': 'ES',
    'spain': 'ES',
    'United States': 'US',
    'united states': 'US',
    'USA': 'US',
    'usa': 'US',
    'United Kingdom': 'GB',
    'united kingdom': 'GB',
    'UK': 'GB',
    'uk': 'GB',
    'Deutschland': 'DE',
    'deutschland': 'DE',
    'Germany': 'DE',
    'germany': 'DE',
    'Italia': 'IT',
    'italia': 'IT',
    'Italy': 'IT',
    'italy': 'IT',
    'Portugal': 'PT',
    'portugal': 'PT',
    'Canada': 'CA',
    'canada': 'CA',
    'Australia': 'AU',
    'australia': 'AU',
    'Brasil': 'BR',
    'brasil': 'BR',
    'Brazil': 'BR',
    'brazil': 'BR',
    'México': 'MX',
    'mexico': 'MX',
    'Mexico': 'MX',
    'Argentina': 'AR',
    'argentina': 'AR',
    'Colombia': 'CO',
    'colombia': 'CO',
    'Chile': 'CL',
    'chile': 'CL',
    'Perú': 'PE',
    'peru': 'PE',
    'Peru': 'PE',
    '日本': 'JP',
    'Japan': 'JP',
    'japan': 'JP',
    '한국': 'KR',
    'Korea': 'KR',
    'korea': 'KR',
    'South Korea': 'KR',
    'south korea': 'KR',
    '中国': 'CN',
    'China': 'CN',
    'china': 'CN',
    'India': 'IN',
    'india': 'IN',
    'Россия': 'RU',
    'Russia': 'RU',
    'russia': 'RU',
    'Nederland': 'NL',
    'netherlands': 'NL',
    'Netherlands': 'NL',
    'België': 'BE',
    'belgium': 'BE',
    'Belgium': 'BE',
    'Schweiz': 'CH',
    'switzerland': 'CH',
    'Switzerland': 'CH',
    'Österreich': 'AT',
    'austria': 'AT',
    'Austria': 'AT',
    'Philippines': 'PH',
    'philippines': 'PH'
  };

  // Get country code from country name
  const getCountryCode = (countryName) => {
    if (!countryName) return null;
    // Try exact match first, then case-insensitive match
    const trimmedName = countryName.trim();
    return countryMapping[trimmedName] || 
           countryMapping[trimmedName.toLowerCase()] || 
           countryMapping[trimmedName.charAt(0).toUpperCase() + trimmedName.slice(1).toLowerCase()];
  };

  // Get flag CSS class for flag-icons
  const getFlagClass = (countryCode) => {
    if (!countryCode) {
      return null;
    }
    // Handle special case for World option
    if (countryCode === 'W') {
      return 'world-flag'; // Special class for world option
    }
    if (countryCode.length !== 2) {
      return null;
    }
    const code = countryCode.toLowerCase();
    return `fi fi-${code}`;
  };

  // Get flag display for a country
  const getFlagDisplay = (countryName) => {
    const countryCode = getCountryCode(countryName);
    if (countryCode) {
      const flagClass = getFlagClass(countryCode);
      return { flagClass, countryCode };
    }
    return { flagClass: null, countryCode: null };
  };

  // Fetch ambassadors data
  useEffect(() => {
    const fetchAmbassadors = async () => {
      try {
        setLoadingAmbassadors(true);
        logger.log('🔍 Fetching ambassadors data...');
        
        const response = await fetch('/.netlify/functions/get-admin-ambassadors');
        const result = await response.json();
        
        if (result.success) {
          logger.log('✅ Ambassadors fetched successfully:', result.data);
          
          // Ensure we always have exactly 5 items for display
          const ambassadorData = result.data || [];
          const filledAmbassadors = [...ambassadorData];
          
          // Fill remaining slots with default data if less than 5
          while (filledAmbassadors.length < 5) {
            filledAmbassadors.push({
              id: `default-${filledAmbassadors.length}`,
              name: 'Ambassador',
              owner: 'PimpMyDrop',
              country: 'France',
              image: null,
              isDefault: true
            });
          }
          
          // Limit to exactly 5 items
          setAmbassadors(filledAmbassadors.slice(0, 5));
        } else {
          logger.error('❌ Failed to fetch ambassadors:', result.message);
          // Set default ambassadors if fetch fails
          setAmbassadors(Array(5).fill(null).map((_, index) => ({
            id: `default-${index}`,
            name: 'Ambassador',
            owner: 'PimpMyDrop',
            country: 'France',
            image: null,
            isDefault: true
          })));
        }
      } catch (error) {
        logger.error('❌ Error fetching ambassadors:', error);
        // Set default ambassadors if error occurs
        setAmbassadors(Array(5).fill(null).map((_, index) => ({
          id: `default-${index}`,
          name: 'Ambassador',
          owner: 'PimpMyDrop',
          country: 'France',
          image: null,
          isDefault: true
        })));
      } finally {
        setLoadingAmbassadors(false);
      }
    };

    fetchAmbassadors();
  }, []);

  const toggleQuestions = () => {
    setShowAllQuestions(!showAllQuestions);
  };
  
  const toggleQuestion = (index) => {
    setOpenQuestionIndex(openQuestionIndex === index ? null : index);
  };

  // Modal functions
  const openSuggestModal = () => {
    setShowSuggestModal(true);
    setStreamerName('');
  };

  const closeSuggestModal = () => {
    setShowSuggestModal(false);
    setStreamerName('');
    setIsSubmitting(false);
  };

  // Toast functions
  const showToastMessage = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const closeToast = () => {
    setShowToast(false);
  };

  const handleSuggestStreamer = async (e) => {
    e.preventDefault();
    if (!streamerName.trim()) return;

    setIsSubmitting(true);
    
    try {
      logger.log('Suggesting streamer:', streamerName.trim());
      
      // Call the netlify function to save the suggestion
      const response = await fetch('/.netlify/functions/suggest-streamer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: streamerName.trim()
        })
      });

      const result = await response.json();
      const suggestedName = streamerName.trim();

      if (result.success) {
        showToastMessage(t('suggestionSuccessThanks', { name: suggestedName }), 'success');
        closeSuggestModal();
      } else if (result.code === 'RATE_LIMIT') {
        showToastMessage(
          t('suggestionRateLimitWait', {
            hours: result.waitHours ?? 0,
            minutes: result.waitMinutes ?? 0
          }),
          'warning'
        );
      } else if (result.code === 'NAME_REQUIRED') {
        showToastMessage(t('suggestStreamerNameRequired'), 'error');
      } else if (result.code === 'NAME_TOO_LONG') {
        showToastMessage(t('suggestStreamerNameTooLong'), 'error');
      } else if (result.code === 'NAME_INVALID_CHARS') {
        showToastMessage(t('suggestStreamerNameInvalid'), 'error');
      } else if (result.code === 'INVALID_JSON') {
        showToastMessage(t('suggestInvalidDataFormat'), 'error');
      } else if (result.code === 'SAVE_FAILED' || response.status >= 500) {
        showToastMessage(t('errorSendingSuggestion'), 'error');
      } else {
        showToastMessage(t('errorSendingSuggestion'), 'error');
      }
      
    } catch (error) {
      logger.error('Error suggesting streamer:', error);
      showToastMessage(t('errorSendingSuggestionRetry'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen ">
      {/* Hero Section */}
      <section className="bg-black relative pt-[100px] flex items-center justify-center overflow-hidden">
        {/* Parallax Background Layers */}
        
        {/* Layer 1: Moving Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20 animate-pulse"></div>
        
        {/* Layer 2: Floating Particles */}
        <div className="absolute inset-0">
          {/* Large floating elements with custom animations */}
          <div className="absolute top-10 left-[10%] w-32 h-32 bg-gradient-to-r from-pink-500/10 to-purple-500/10 rounded-full blur-xl parallax-slow-float"></div>
          <div className="absolute top-32 right-[15%] w-24 h-24 bg-gradient-to-r from-cyan-500/15 to-blue-500/15 rounded-full blur-lg parallax-glow-pulse"></div>
          <div className="absolute bottom-20 left-[20%] w-28 h-28 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-full blur-xl parallax-float" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-40 right-[25%] w-20 h-20 bg-gradient-to-r from-green-500/15 to-teal-500/15 rounded-full blur-lg parallax-glow-pulse" style={{animationDelay: '1s'}}></div>
          
          {/* Medium floating particles */}
          <div className="absolute top-1/4 left-[30%] w-12 h-12 bg-pink-500/20 rounded-full parallax-twinkle"></div>
          <div className="absolute top-3/4 right-[40%] w-8 h-8 bg-cyan-400/30 rounded-full parallax-float" style={{animationDelay: '3s'}}></div>
          <div className="absolute top-1/2 left-[60%] w-10 h-10 bg-yellow-400/25 rounded-full parallax-glow-pulse" style={{animationDelay: '4s'}}></div>
          
          {/* Small sparkle particles */}
          <div className="absolute top-16 left-[45%] w-3 h-3 bg-white/40 rounded-full parallax-twinkle" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-64 right-[55%] w-2 h-2 bg-pink-300/60 rounded-full parallax-twinkle" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-32 left-[70%] w-4 h-4 bg-cyan-300/50 rounded-full parallax-twinkle" style={{animationDelay: '3s'}}></div>
          <div className="absolute bottom-16 right-[30%] w-3 h-3 bg-yellow-300/40 rounded-full parallax-twinkle" style={{animationDelay: '0.5s'}}></div>
          
          {/* Drifting elements */}
          <div className="absolute top-1/3 w-6 h-6 bg-purple-400/20 rounded-full blur-sm parallax-drift" style={{animationDelay: '0s'}}></div>
          <div className="absolute top-2/3 w-4 h-4 bg-blue-400/25 rounded-full blur-sm parallax-drift" style={{animationDelay: '10s'}}></div>
          <div className="absolute top-1/2 w-5 h-5 bg-pink-400/20 rounded-full blur-sm parallax-drift" style={{animationDelay: '20s'}}></div>
        </div>
        
        {/* Layer 3: Geometric Shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-[80%] w-16 h-16 border-2 border-pink-500/30 rotate-45 animate-spin parallax-slow-float" style={{animationDuration: '20s'}}></div>
          <div className="absolute bottom-32 left-[10%] w-12 h-12 border border-cyan-400/40 rotate-12 parallax-glow-pulse"></div>
          <div className="absolute top-1/2 right-[10%] w-20 h-20 border-2 border-yellow-500/25 rotate-45 animate-spin parallax-float" style={{animationDuration: '15s'}}></div>
          <div className="absolute top-40 left-[50%] w-8 h-8 border border-purple-400/35 rotate-45 parallax-twinkle"></div>
          <div className="absolute bottom-60 right-[60%] w-14 h-14 border-2 border-green-500/30 rotate-12 parallax-slow-float" style={{animationDelay: '3s'}}></div>
        </div>
        
        {/* Layer 4: Moving Lines */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-pink-500/30 to-transparent parallax-wave"></div>
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent parallax-wave" style={{animationDelay: '5s'}}></div>
          <div className="absolute top-0 left-0 h-full w-px bg-gradient-to-b from-transparent via-purple-500/20 to-transparent animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-0 right-0 h-full w-px bg-gradient-to-b from-transparent via-blue-500/20 to-transparent animate-pulse" style={{animationDelay: '3s'}}></div>
          
          {/* Diagonal moving lines */}
          <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent rotate-12 parallax-wave" style={{animationDelay: '8s'}}></div>
          <div className="absolute bottom-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-400/20 to-transparent -rotate-12 parallax-wave" style={{animationDelay: '12s'}}></div>
        </div>
        
        {/* Layer 5: Stars Field */}
        <div className="absolute inset-0">
          <div className="absolute top-8 left-[15%] w-1 h-1 bg-white/60 rounded-full parallax-twinkle"></div>
          <div className="absolute top-24 left-[85%] w-1 h-1 bg-white/40 rounded-full parallax-twinkle" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-48 left-[25%] w-1 h-1 bg-white/50 rounded-full parallax-twinkle" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-72 left-[75%] w-1 h-1 bg-white/30 rounded-full parallax-twinkle" style={{animationDelay: '3s'}}></div>
          <div className="absolute bottom-24 left-[35%] w-1 h-1 bg-white/60 rounded-full parallax-twinkle" style={{animationDelay: '4s'}}></div>
          <div className="absolute bottom-48 left-[65%] w-1 h-1 bg-white/40 rounded-full parallax-twinkle" style={{animationDelay: '0.5s'}}></div>
          <div className="absolute bottom-16 left-[55%] w-1 h-1 bg-white/50 rounded-full parallax-twinkle" style={{animationDelay: '2.5s'}}></div>
          <div className="absolute top-32 left-[40%] w-1 h-1 bg-white/45 rounded-full parallax-twinkle" style={{animationDelay: '1.5s'}}></div>
          <div className="absolute top-56 left-[90%] w-1 h-1 bg-white/35 rounded-full parallax-twinkle" style={{animationDelay: '3.5s'}}></div>
          <div className="absolute bottom-40 left-[20%] w-1 h-1 bg-white/55 rounded-full parallax-twinkle" style={{animationDelay: '4.5s'}}></div>
        </div>
        
        {/* Layer 6: Depth Orbs */}
        <div className="absolute inset-0">
          <div className="absolute top-16 left-[5%] w-64 h-64 bg-gradient-radial from-pink-500/5 to-transparent rounded-full blur-3xl parallax-slow-float"></div>
          <div className="absolute bottom-16 right-[5%] w-48 h-48 bg-gradient-radial from-cyan-500/8 to-transparent rounded-full blur-3xl parallax-glow-pulse" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-gradient-radial from-purple-500/4 to-transparent rounded-full blur-3xl parallax-float" style={{animationDelay: '4s'}}></div>
        </div>
        
        {/* Layer 7: Overlay Pattern */}
        <div className="absolute inset-0 bg-black/20"></div>

        <div className="container mx-auto px-6 relative z-10 pb-[100px]">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Side - Hero Image */}
            <div className="relative order-1 lg:order-1">
              <div className="relative z-10">
                <img 
                  src="https://res.cloudinary.com/dez2cotie/image/upload/v1751400542/Banner_Visual_PimpMyDrop_lbmevj.webp"
                  alt="Révolutionne ton stream - Design Platform"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
                
                {/* Floating Elements */}
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-2xl">
                  ⚡
                </div>
                
                <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center text-xl">
                  🎨
                </div>
              </div>
            </div>

            {/* Right Side - Content */}
            <div className="text-center lg:text-left space-y-8 order-2 lg:order-2">
              <div className="space-y-4">
                <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight italic" style={{ fontFamily: 'cursive, Arial, sans-serif' }}>
                  <span className="text-white">{t('giveLifeToCommunity')}</span>
                </h1>
                
                <p className="text-xl text-gray-300 max-w-2xl leading-relaxed">
                {t('ultimatePlatformStreamers')}
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button className="group relative px-8 py-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl font-bold text-black text-lg hover:from-yellow-500 hover:to-orange-600 transform hover:scale-105 transition-all duration-300 shadow-2xl">
                  <span className="flex items-center justify-center gap-2">
                    {t('createDropZone')} →
                  </span>
                  <div onClick={() => window.location.href = '/application'} className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl blur opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                </button>
                
                <button 
                  onClick={() => {
                    const ambassadorsSection = document.getElementById('ambassadors-section');
                    if (ambassadorsSection) {
                      ambassadorsSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="relative z-[1] px-8 py-4 border-2 border-gray-400 text-gray-300 rounded-xl font-bold text-lg hover:bg-gray-700 hover:text-white transform hover:scale-105 transition-all duration-300"
                >
                  {t('seeDropZones')}
                </button>

                  <div className="absolute w-[20rem] ml-[400px] mt-[-110px] rotate-[324deg] animate-float-hat">
                    <img src="https://res.cloudinary.com/dez2cotie/image/upload/v1751400564/Baseball_Cap.K10.2k_bltkgw.webp" alt="Baseball Cap Decoration" />
                  </div>
              </div>

        
            </div>

          </div>
        </div>
      </section>

      {/* Rules Section */}
      <section className="py-20 bg-black bg-opacity-50 relative overflow-hidden">
        <div className="container mx-auto px-6">
          
          {/* Title */}
          <div className="text-center space-y-6 mb-16">
            <h2 className="text-4xl lg:text-6xl font-bold text-white leading-tight italic" style={{ fontFamily: 'cursive, Arial, sans-serif' }}>
              {t('threeRolesMission')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-cyan-500">{t('oneMission')}</span> :
            </h2>
            <p className="text-2xl lg:text-3xl font-bold text-white italic" style={{ fontFamily: 'cursive, Arial, sans-serif' }}>
              {t('unlockCreativity')}
            </p>
          </div>

          {/* Three Role Cards */}
          <div className="relative grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            <div className="absolute w-[8rem] rotate-[7deg] mt-[50px] ml-[100%]">
              <img src="https://res.cloudinary.com/dez2cotie/image/upload/v1751888935/arrow-steam_xwjymo.png" alt="PimpMyDrop T-Shirt" className="w-full h-full object-contain" />
            </div>


            {/* Content Creator Card */}
            <div className="relative bg-black border-4 border-purple-500 rounded-3xl p-8 text-center hover:scale-105 transition-transform duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-3xl"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">🎨</span>
                </div>
                <h3 className="text-2xl font-bold text-purple-400 uppercase">{t('creator')}</h3>
                <h4 className="text-xl font-bold text-purple-400 mb-6 uppercase">{t('contentCreator')}</h4>
                
                <div className="text-left space-y-3 text-sm text-gray-300">
                  <div className="space-y-4 pb-[30px]">
                    {/* Main question */}

                    <p dangerouslySetInnerHTML={{ __html: '• ' + t('earn30Percent') }}></p>
                    <p>• {t('diversifyRevenue')}</p>
                    <p dangerouslySetInnerHTML={{ __html: '• ' + t('strengthenEngagement') }}></p>
                    <p dangerouslySetInnerHTML={{ __html: '• ' + t('hundredPercentManaged') }}></p>
                    <p>• {t('createStrongBond')}</p>
                  </div> 
                </div>
              </div>

              <div className="mt-6 pt-4 absolute left-[50%] bottom-[-60px] transform -translate-x-1/2 -translate-y-1/2 w-[250px]">
                <button 
                  onClick={() => window.location.href = '/application'}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-3 px-6 rounded-full hover:from-purple-600 hover:to-pink-600 transition-all duration-300 cursor-pointer"
                >
                  {t('createDropZone')}
                </button>
              </div>
            </div>

            {/* Artist/Designer Card */}
            <div className="relative bg-black border-4 border-cyan-500 rounded-3xl p-8 text-center hover:scale-105 transition-transform duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-3xl"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">🎭</span>
                </div>

                <h3 className="text-2xl font-bold text-cyan-400 uppercase">{t('artistDesigner')}</h3>
                <h4 className="text-xl font-bold text-cyan-400 mb-6 uppercase">{t('promptKing')}</h4>
                
                <div className="text-left space-y-3 text-sm text-gray-300">
                  <div className="space-y-4 pb-[30px]">
                    {/* Main question */}

                    <p dangerouslySetInnerHTML={{ __html: '• ' + t('propose10Percent') }}></p>
                    <p dangerouslySetInnerHTML={{ __html: '• ' + t('getArtistBadge') }}></p>
                    <p dangerouslySetInnerHTML={{ __html: '• ' + t('participateGiveaways') }}></p>
                    <p>• {t('expressFreelyCreativity')}</p>

                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 absolute left-[50%] bottom-[-60px] transform -translate-x-1/2 -translate-y-1/2 w-[250px]">
                <button 
                  onClick={() => {
                    // Use same logic as Header component for Submit navigation
                    // const pathname = location.pathname;
                    // const submitPath = pathname === '/' && firstAmbassador ? `/${firstAmbassador}/submit` : '/submit';
                    const submitPath = '/submit';
                    navigate(submitPath);
                  }}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold py-3 px-6 rounded-full hover:from-cyan-600 hover:to-blue-600 transition-all duration-300"
                >
                  {t('proposeCreation')}
                </button>
              </div>

            </div>

            {/* Community Member Card */}
            <div className="relative bg-black border-4 border-yellow-500 rounded-3xl p-8 text-center hover:scale-105 transition-transform duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-3xl"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">👥</span>
                </div>
                <h3 className="text-2xl font-bold text-yellow-400 uppercase">{t('member')}</h3>
                <h4 className="text-xl font-bold text-yellow-400 mb-6 uppercase">{t('communityMember')}</h4>
                
                <div className="text-left space-y-3 text-sm text-gray-300">
                  <div className="space-y-4 pb-[30px]">
                    {/* Main question */}

                    <p>• {t('supportCreators')}</p>
                    <p dangerouslySetInnerHTML={{ __html: '• ' + t('becomeVIP') }}></p>
                    <p dangerouslySetInnerHTML={{ __html: '• ' + t('winGoldenTicket') }}></p>
                    <p dangerouslySetInnerHTML={{ __html: '• ' + t('exclusiveDigitalContent') }}></p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 absolute left-[50%] bottom-[-60px] transform -translate-x-1/2 -translate-y-1/2 w-[250px]">
                <button 
                  onClick={() => {
                    // Smooth scroll to ambassadors section
                    const ambassadorsSection = document.getElementById('ambassadors-section');
                    if (ambassadorsSection) {
                      ambassadorsSection.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }
                  }}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold py-3 px-6 rounded-full hover:from-cyan-600 hover:to-blue-600 transition-all duration-300"
                >
                  {t('discoverDropZones')}
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Ambassadors Section */}
      <section id="ambassadors-section" className="pt-20 bg-gray-800">
        <div className="container mx-auto px-6">
          
          {/* Title */}
          <div className="text-center space-y-6 mb-16">
            <div className="absolute w-[13rem] rotate-[32deg] mt-[-80px] animate-float-tshirt">
              <img src="https://res.cloudinary.com/dez2cotie/image/upload/v1752146286/pm6_ez3xe6.webp" alt="PimpMyDrop T-Shirt" className="w-full h-full object-contain" />
            </div>


            <h2 className="relative text-4xl lg:text-5xl font-bold text-white leading-tight italic" style={{ fontFamily: 'cursive, Arial, sans-serif' }}>
              {t('dropZonesToDiscover')}
            </h2>
          </div>

          {/* Ambassadors Display */}
          <div className="flex flex-col items-center">
            {loadingAmbassadors ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-600 border-t-pink-500"></div>
              </div>
            ) : (
              <div className="flex flex-wrap justify-center gap-8 lg:gap-12">
                {ambassadors.map((ambassador, index) => (
                  <div key={ambassador.id} className="flex flex-col items-center group">
                                         {/* Ambassador Image */}
                     <div className="relative mb-4">
                       {ambassador.id.toString().startsWith('default-') ? (
                         // Dummy data - Show question mark circle
                         <div className="w-40 h-40 lg:w-48 lg:h-48 rounded-full border-4 border-gray-600 bg-gray-700 flex items-center justify-center">
                             <span className="text-[120px]">?</span>
                         </div>
                       ) : (
                         // Real data - Show ambassador image
                         <a 
                           href={`/${ambassador.owner}`}
                           className="w-40 h-40 lg:w-48 lg:h-48 rounded-full overflow-hidden border-4 border-gray-600 group-hover:border-pink-500 transition-colors duration-300 shadow-xl cursor-pointer hover:border-pink-400 block"
                         >
                         <img
                           src={(() => {
                             // Handle image - get from logo array
                             if (ambassador.image) {
                               try {
                                 // Check if it's the new object format: {profile: [...], logo: [...]}
                                 if (typeof ambassador.image === 'string' && ambassador.image.startsWith('{')) {
                                   const imageData = JSON.parse(ambassador.image);
                                   // Get logo image (first from logo array)
                                   if (imageData.logo && Array.isArray(imageData.logo) && imageData.logo.length > 0) {
                                     return imageData.logo[0] || defaultImage;
                                   }
                                   // Fallback to first profile image if logo not available
                                   if (imageData.profile && Array.isArray(imageData.profile) && imageData.profile.length > 0) {
                                     return imageData.profile[0] || defaultImage;
                                   }
                                   return defaultImage;
                                 }
                                 // Check if it's old JSON array string format
                                 else if (typeof ambassador.image === 'string' && ambassador.image.startsWith('[')) {
                                   const imageUrls = JSON.parse(ambassador.image);
                                   return imageUrls[0] || defaultImage; // Get first image
                                 }
                                 // Check if it's already an array
                                 else if (Array.isArray(ambassador.image)) {
                                   return ambassador.image[0] || defaultImage; // Get first image
                                 }
                                 // Single image string
                                 else {
                                   return ambassador.image;
                                 }
                               } catch (error) {
                                 console.warn('Error parsing ambassador image:', error);
                                 return ambassador.image || defaultImage;
                               }
                             }
                             return defaultImage;
                           })()}
                            alt={ambassador.owner}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            onError={(e) => {
                              e.target.src = defaultImage;
                            }}
                          />
                         </a>
                       )}
                      

                    </div>
                    
                                         {/* Ambassador Info */}
                     <div className="text-center">
                       {ambassador.id.toString().startsWith('default-') ? (
                         // Dummy data - Show buttons instead of name/country
                         <div className="space-y-2 mt-4 w-48">
                           <a 
                             href="/application"
                            className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 px-3 rounded-lg text-xs transition-colors duration-300 transform hover:scale-105 w-full cursor-pointer inline-block text-center"
                          >
                            {t('createDropZoneEmoji')}
                          </a>
                         <button 
                           onClick={openSuggestModal}
                           className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-3 rounded-lg text-xs transition-colors duration-300 transform hover:scale-105 w-full"
                         >
                           {t('suggestCreator')}
                         </button>
                         </div>
                       ) : (
                         // Real data - Show name, country and create button
                         <>
                           <h3 className="text-xl font-bold text-white mb-1 group-hover:text-pink-400 transition-colors duration-300 uppercase">
                             {ambassador.owner}
                           </h3>
                           <p className="text-gray-400 text-sm mb-2 flex items-center justify-center gap-2">
                             {(() => {
                               const { flagClass, countryCode } = getFlagDisplay(ambassador.country);
                               if (countryCode === 'W') {
                                 return <span style={{ fontSize: '16px' }}>🌍</span>;
                               } else if (flagClass) {
                                 return <span className={flagClass}></span>;
                               } else {
                                 return <span>🇫🇷</span>;
                               }
                             })()}
                             <span>{ambassador.country}</span>
                           </p>
                         </>
                       )}
                     </div>
                  </div>
                ))}
              </div>
            )}

            {/* Navigation Arrows - Only show when there are more than 5 real ambassadors */}
            {(() => {
              const realAmbassadorsCount = ambassadors.filter(amb => !amb.id.toString().startsWith('default-')).length;
              return realAmbassadorsCount > 5 ? (
                <div className="flex items-center justify-center gap-8 mt-12">
                  <button className="w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center text-white transition-colors duration-300 transform hover:scale-110">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  <div className="flex gap-2">
                    {[0, 1, 2, 3, 4].map((dot) => (
                      <div
                        key={dot}
                        className={`w-3 h-3 rounded-full ${dot === 0 ? 'bg-pink-500' : 'bg-gray-600'} transition-colors duration-300`}
                      ></div>
                    ))}
                  </div>
                  
                  <button className="w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center text-white transition-colors duration-300 transform hover:scale-110">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              ) : null;
            })()}
            
            {/* More Streamer Button - Only show if 6 or more streamers */}
            {ambassadors.filter(amb => !amb.id.toString().startsWith('default-')).length >= 6 && (
              <div className="flex justify-center mt-12 mb-6">
                <button
                  onClick={() => navigate('/streamer')}
                  className="text-white font-medium px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
                >
                  {t('moreStreamers')}
                </button>
              </div>
            )}
          </div>

        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-black bg-opacity-75 py-16 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-300 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]" style={{ fontFamily: 'cursive, Arial, sans-serif' }}>{t('frequentQuestions')}</h2>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {/* Question 1 */}
            <div className="bg-[#18181B]  overflow-hidden">
              <button 
                onClick={() => toggleQuestion(0)}
                className="w-full py-4 px-6 text-left flex justify-between items-center text-white hover:bg-[#27272A] transition-colors duration-300"
              >
                <span className="font-medium">{t('howVotingWorks')}</span>
                <i className={`fa-solid fa-chevron-${openQuestionIndex === 0 ? 'up' : 'down'} text-cyan-500`}></i>
              </button>
              <div className={`${openQuestionIndex === 0 ? 'block' : 'hidden'} px-6 pb-4 text-gray-300`}>
                <p>{t('eachMemberCanVote')}</p>
                <p>{t('mostPopularProduced')}</p>  
              </div>
            </div>
            
            {/* Question 2 */}
            <div className="bg-[#18181B]  overflow-hidden">
              <button 
                onClick={() => toggleQuestion(1)}
                className="w-full py-4 px-6 text-left flex justify-between items-center text-white hover:bg-[#27272A] transition-colors duration-300"
              >
                <span className="font-medium">{t('whenWinnersAnnounced')}</span>
                <i className={`fa-solid fa-chevron-${openQuestionIndex === 1 ? 'up' : 'down'} text-cyan-500`}></i>
              </button>
              <div className={`${openQuestionIndex === 1 ? 'block' : 'hidden'} px-6 pb-4 text-gray-300`}>
                <p>{t('endOfVotingPeriod')}</p>
              </div>
            </div>
            
            {/* Question 3 */}
            <div className="bg-[#18181B]  overflow-hidden">
              <button 
                onClick={() => toggleQuestion(2)}
                className="w-full py-4 px-6 text-left flex justify-between items-center text-white hover:bg-[#27272A] transition-colors duration-300"
              >
                <span className="font-medium">{t('howSubmitCreation')}</span>
                <i className={`fa-solid fa-chevron-${openQuestionIndex === 2 ? 'up' : 'down'} text-cyan-500`}></i>
              </button>
              <div className={`${openQuestionIndex === 2 ? 'block' : 'hidden'} px-6 pb-4 text-gray-300`}>
                <p>{t('submitTabUpload')}</p>
              </div>
            </div>
            
            {/* Question 4 */}
            <div className="bg-[#18181B]  overflow-hidden">
              <button 
                onClick={() => toggleQuestion(3)}
                className="w-full py-4 px-6 text-left flex justify-between items-center text-white hover:bg-[#27272A] transition-colors duration-300"
              >
                <span className="font-medium">{t('howMuchEarn')}</span>
                <i className={`fa-solid fa-chevron-${openQuestionIndex === 3 ? 'up' : 'down'} text-cyan-500`}></i>
              </button>
              <div className={`${openQuestionIndex === 3 ? 'block' : 'hidden'} px-6 pb-4 text-gray-300`}>
                <p>{t('earn10PercentBenefits')}</p>
              </div>
            </div>
            
            {/* Question 5 */}
            <div className="bg-[#18181B]  overflow-hidden">
              <button 
                onClick={() => toggleQuestion(4)}
                className="w-full py-4 px-6 text-left flex justify-between items-center text-white hover:bg-[#27272A] transition-colors duration-300"
              >
                <span className="font-medium">{t('whichProducts')}</span>
                <i className={`fa-solid fa-chevron-${openQuestionIndex === 4 ? 'up' : 'down'} text-cyan-500`}></i>
              </button>
              <div className={`${openQuestionIndex === 4 ? 'block' : 'hidden'} px-6 pb-4 text-gray-300`}>
                {t('creationsProducedSupports')}
              </div>
            </div>
            
            {/* Additional Questions - Initially Hidden */}
            {showAllQuestions && (
              <>
                {/* Question 6 */}
                <div className="bg-[#18181B]  overflow-hidden">
                  <button 
                    onClick={() => toggleQuestion(5)}
                    className="w-full py-4 px-6 text-left flex justify-between items-center text-white hover:bg-[#27272A] transition-colors duration-300"
                  >
                    <span className="font-medium">{t('whatDeadline')}</span>
                    <i className={`fa-solid fa-chevron-${openQuestionIndex === 5 ? 'up' : 'down'} text-cyan-500`}></i>
                  </button>
                  <div className={`${openQuestionIndex === 5 ? 'block' : 'hidden'} px-6 pb-4 text-gray-300`}>
                    <p>{t('noDeadline')}</p>
                    <p>{t('onlySpecialEvents')}</p>
                  </div>
                </div>
                
                {/* Question 7 */}
                <div className="bg-[#18181B]  overflow-hidden">
                  <button 
                    onClick={() => toggleQuestion(6)}
                    className="w-full py-4 px-6 text-left flex justify-between items-center text-white hover:bg-[#27272A] transition-colors duration-300"
                  >
                    <span className="font-medium">{t('moreDropsFuture')}</span>
                    <i className={`fa-solid fa-chevron-${openQuestionIndex === 6 ? 'up' : 'down'} text-cyan-500`}></i>
                  </button>
                  <div className={`${openQuestionIndex === 6 ? 'block' : 'hidden'} px-6 pb-4 text-gray-300`}>
                    <p>{t('yesNewDrops')}</p>
                  </div>
                </div>
              </>
            )}
            
            {/* Toggle Button */}
            <div className="text-center pt-4">
              <button 
                onClick={toggleQuestions}
                className="text-cyan-500 hover:text-cyan-400 transition-colors duration-300 flex items-center mx-auto"
              >
                {showAllQuestions ? t('seeLess') : t('seeMore')}
                <i className={`fa-solid fa-chevron-${showAllQuestions ? 'up' : 'down'} ml-2`}></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Suggest Streamer Modal */}
      {showSuggestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">{t('proposeStreamer')}</h3>
              <button
                onClick={closeSuggestModal}
                className="text-gray-400 hover:text-white transition-colors duration-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSuggestStreamer} className="space-y-4">
              <div>
                <label htmlFor="streamerName" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('streamerName')}
                </label>
                <input
                  type="text"
                  id="streamerName"
                  value={streamerName}
                  onChange={(e) => setStreamerName(e.target.value)}
                  placeholder={t('enterStreamerName')}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeSuggestModal}
                  className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-300"
                  disabled={isSubmitting}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting || !streamerName.trim()}
                >
                  {isSubmitting ? t('sending') : t('suggest')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <Toast 
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={closeToast}
        duration={4000}
      />
    </div>
  );
}

export default Home;